import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Clock, Lock, Send, UserX } from 'lucide-react';
import { formatCents, formatDate, todayIso, type Lease, type LeaseParty } from '@vuokramani/shared';
import { ContactValue, DetailList, type DetailRow } from '../../components/page.tsx';
import { Alert, Button, Dialog, DialogActions, Panel, TextField } from '../../components/ui.tsx';
import { errorKey } from '../../lib/api.ts';
import { contractUrl, useInviteParty } from '../../lib/tenancy.ts';
import s from './tenancy.module.css';

/** Vuokrasuhteen ehdot riveinä. Samat rivit näkyvät vuokranantajalle ja vuokralaiselle. */
export function leaseRows(l: Lease, t: TFunction, lang: string): DetailRow[] {
  const money = (cents: number) => formatCents(cents, lang);
  const water =
    l.waterBilling === 'per_person' && l.waterCents !== null
      ? t('lease.waterPerPerson', { amount: money(l.waterCents) })
      : t(`lease.water.${l.waterBilling}`);
  return [
    { label: t('lease.rent'), value: t('lease.perMonth', { amount: money(l.rentCents) }) },
    { label: t('lease.dueDay'), value: t('lease.dueDayValue', { day: l.dueDay }) },
    { label: t('lease.deposit'), value: money(l.depositCents) },
    { label: t('lease.waterLabel'), value: water },
    { label: t('lease.electricityLabel'), value: t(`lease.electricity.${l.electricityBilling}`) },
    { label: t('lease.start'), value: formatDate(l.startDate, lang) },
    {
      label: t('lease.duration'),
      // Irtisanotun toistaiseksi voimassa olevan päättymispäivä näkyy omalla rivillään.
      value:
        l.endDate && (!l.terminatedBy || l.terminatedBy === 'fixed_term')
          ? t('lease.fixedUntil', { date: formatDate(l.endDate, lang) })
          : t('lease.indefinite'),
    },
    ...(l.terminatedBy && l.endDate
      ? [
          {
            label: t(l.endDate < todayIso() ? 'lease.ended' : 'lease.ends'),
            value: t('lease.endsValue', {
              date: formatDate(l.endDate, lang),
              by: t(`ending.by.${l.terminatedBy}`),
            }),
          },
        ]
      : []),
    ...(l.rentIncreaseTerms ? [{ label: t('lease.increase'), value: l.rentIncreaseTerms }] : []),
    ...(l.signedOn
      ? [
          {
            label: t('lease.signed'),
            value: t(`lease.signedWith.${l.signedMethod ?? 'paper'}`, {
              date: formatDate(l.signedOn, lang),
            }),
          },
        ]
      : []),
    ...(l.contractFile
      ? [
          {
            label: t('lease.contractFile'),
            value: (
              <a href={contractUrl(l.id)} download>
                {l.contractFile.name}
              </a>
            ),
          },
        ]
      : []),
  ];
}

export function LockNote() {
  const { t } = useTranslation();
  return (
    <p className={s.lockNote}>
      <Lock size={16} aria-hidden="true" />
      <span>{t('lease.locked')}</span>
    </p>
  );
}

export function LeaseTerms({ lease }: { lease: Lease }) {
  const { t, i18n } = useTranslation();
  return (
    <Panel>
      {lease.status === 'active' && <LockNote />}
      <DetailList rows={leaseRows(lease, t, i18n.language)} />
    </Panel>
  );
}

/**
 * Osapuolet: sopimuksen vuokralaiset yhteystietoineen. Vuokranantajalle (manage) näytetään
 * myös, onko vuokralainen kutsuttu sovellukseen, ja kutsupainike. Muut asukkaat ja
 * lemmikit ovat erillisessä, muokattavassa listassa (Occupants.tsx).
 */
export function LeaseParties({ lease, manage = false }: { lease: Lease; manage?: boolean }) {
  const { t } = useTranslation();
  const [inviting, setInviting] = useState<LeaseParty | null>(null);
  const tenants = lease.parties.filter((p) => p.role === 'tenant');
  return (
    <Panel>
      <DetailList
        rows={tenants.map((p, i) => ({
          label: i === 0 ? t('lease.tenants', { count: tenants.length }) : '',
          value: (
            <>
              <ContactValue
                name={p.isMe ? `${p.name} (${t('lease.you')})` : p.name}
                phone={p.phone}
                email={p.email}
              />
              {manage && p.access !== 'joined' && (
                <span className={s.accessTag}>
                  {p.access === 'invited' ? (
                    <Clock size={13} aria-hidden="true" />
                  ) : (
                    <UserX size={13} aria-hidden="true" />
                  )}
                  {t(`lease.access.${p.access}`)}
                  <Button variant="ghost" onClick={() => setInviting(p)}>
                    <Send size={14} aria-hidden="true" />
                    {p.access === 'invited' ? t('lease.reinvite') : t('lease.invite')}
                  </Button>
                </span>
              )}
            </>
          ),
        }))}
      />
      {inviting && (
        <InviteDialog
          propertyId={lease.propertyId}
          party={inviting}
          onClose={() => setInviting(null)}
        />
      )}
    </Panel>
  );
}

function InviteDialog({
  propertyId,
  party,
  onClose,
}: {
  propertyId: string;
  party: LeaseParty;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const invite = useInviteParty(propertyId);
  const [email, setEmail] = useState(party.email ?? '');
  const needsEmail = !party.email;
  return (
    <Dialog open onClose={onClose} title={t('lease.inviteTitle', { name: party.name })}>
      <form
        style={{ display: 'grid', gap: '1rem' }}
        onSubmit={(e) => {
          e.preventDefault();
          invite.mutate(
            { partyId: party.id, email: needsEmail ? email.trim() : undefined },
            { onSuccess: onClose },
          );
        }}
      >
        <p>{t('lease.inviteText')}</p>
        {needsEmail ? (
          <TextField
            label={t('newLease.tenantEmail')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        ) : (
          <p>
            <strong>{party.email}</strong>
          </p>
        )}
        {invite.isError && <Alert>{t(errorKey(invite.error))}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={invite.isPending || (needsEmail && !email.includes('@'))}
          >
            {t('lease.sendInvite')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
